import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { appliedCapTooltipInputs, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import type { StrategyPresentation } from './types';

// Lincoln MoneyGuard Fixed Advantage is structurally identical to OneAmerica
// Asset Care: a two-pool reimbursement hybrid drawn Face-first, then
// Continuation of Benefits (COB). The presentation mirrors oaAssetCarePresentation
// but uses MoneyGuard's product wording — the base pool is the Face / Specified
// Amount (not "Acceleration of Benefits"), and indemnity is capped at 80% of the
// Monthly Maximum (vs Asset Care's 75%). See lincolnMoneyGuardStrategy.ts.

export const lincolnMoneyGuardPresentation: StrategyPresentation = {
  // MoneyGuard's monthly cap is per care environment and inflates on the
  // Face/base rate (not the policy-wide rate the default BenefitAmountCell
  // uses), so the cell headlines the engine's own applied cap; the tooltip's
  // base × (1+r)^n derivation is reconstructed from terms for display only.
  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const { baseMonthly, rate, capYears } = appliedCapTooltipInputs(month, terms);
    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    // Exponent = policyYear − 1, capped at capYears, matching the engine's
    // `compoundFactor` so the tooltip's base × (1+r)^n equals the displayed result.
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="leading-tight cursor-help">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear}`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `Inflation capped at year ${cap}` : undefined}
          compoundLabel="Compound growth"
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC pool breakdown"
        description="MoneyGuard pays from the Face Amount (LTC acceleration) first, then Continuation of Benefits (COB). Inflation accrues at policy anniversaries while each pool remains open."
        sections={[
          {
            heading: 'Face Amount (LTC acceleration)',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Inflation accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
          },
          {
            heading: 'Continuation of Benefits (COB)',
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const baseDrawdown = month.deltas.base.depletion;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? `Face drew ${currency(baseDrawdown)}; death benefit reduction capped at the Residual Death Benefit ${currency(close)}.`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="For MoneyGuard, only Face Amount drawdown reduces the death benefit. COB drawdown does not reduce death benefit. The death benefit is floored at the Residual Death Benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced this month', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    if (eligibleDays === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
    // OOP reasons. Their sum is the total out-of-pocket, and
    // cost = paid + oopOverCap + (oopLt2Adl + oopWp + oopIl) — the waterfall
    // below. We read the calc-supplied per-day breakdown rather than a single
    // "ineligible days" bucket so a WP-only row never claims "<2 ADL"
    // contributed, and a transition month splits its cost honestly.
    const oopOverCap = Math.max(eligibleCost - paid, 0);
    const breakdown = month.events.ineligibleDayBreakdown;
    const oopLt2Adl = breakdown.lt2AdlDays * dailyCost;
    const oopWp = breakdown.inWpDays * dailyCost;
    const oopIl = breakdown.ilNotCoveredDays * dailyCost;
    const oopPrePolicy = breakdown.prePolicyDays * dailyCost;
    const totalOop = oopOverCap + oopLt2Adl + oopWp + oopIl + oopPrePolicy;

    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? 'Pays a fixed cash benefit (capped at 80% of the monthly maximum) while the Face pool has balance, then reimburses from COB.'
      : 'Reimburses eligible incurred cost, up to the monthly cap.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Care cost</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          {isPartial && (
            <>
              <span className="text-gray-300">
                Eligible ({eligibleDays}/{DAYS_IN_MONTH} days)
              </span>
              <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
            </>
          )}
          <span className="text-gray-300">Policy paid</span>
          <span className="font-semibold tabular-nums">−{currency(paid)}</span>
          <span className="text-gray-300">Out of pocket</span>
          <span className="font-semibold tabular-nums text-rose-300">{currency(totalOop)}</span>
          {oopOverCap > 0 && (
            <>
              <span className="pl-2 text-gray-400">· over cap</span>
              <span className="tabular-nums text-rose-300">{currency(oopOverCap)}</span>
            </>
          )}
          {oopWp > 0 && (
            <>
              <span className="pl-2 text-gray-400">
                · waiting period <span className="text-gray-500">· {breakdown.inWpDays}d</span>
              </span>
              <span className="tabular-nums text-rose-300">{currency(oopWp)}</span>
            </>
          )}
          {oopLt2Adl > 0 && (
            <>
              <span className="pl-2 text-gray-400">
                {'· <2 ADL'} <span className="text-gray-500">· {breakdown.lt2AdlDays}d</span>
              </span>
              <span className="tabular-nums text-rose-300">{currency(oopLt2Adl)}</span>
            </>
          )}
          {oopIl > 0 && (
            <>
              <span className="pl-2 text-gray-400">
                · independent living
                <span className="text-gray-500"> · {breakdown.ilNotCoveredDays}d</span>
              </span>
              <span className="tabular-nums text-rose-300">{currency(oopIl)}</span>
            </>
          )}
          {oopPrePolicy > 0 && (
            <>
              <span className="pl-2 text-gray-400">
                · before policy start
                <span className="text-gray-500"> · {breakdown.prePolicyDays}d</span>
              </span>
              <span className="tabular-nums text-rose-300">{currency(oopPrePolicy)}</span>
            </>
          )}
        </div>
        <div className="mt-1 text-[10px] text-gray-400">
          Drawn from Face {currency(drawnBase)}
          {drawnEob > 0 ? ` + COB ${currency(drawnEob)}` : ''}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;

    if (!(wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0)) return null;

    return (
      <div className="mt-0.5 text-[10px] text-amber-700">
        {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
      </div>
    );
  },

  // MoneyGuard's monthly cap uses the Face/base inflation rate.
  benefitInflationRate: terms =>
    terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. MoneyGuard draws from the Face Amount (LTC acceleration) first, then Continuation of Benefits (COB). Inflation can accrue at policy anniversaries while pools remain open."
        formulas={[
          { label: 'Closing pool', expression: '(opening Face + opening COB) + inflation accrual − drawn this month' },
          { label: 'Draw order', expression: 'Face first; COB engages once the Face pool is exhausted' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity, depending on policy mode), with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          { label: 'Reimbursement mode', expression: 'min(eligible cost, monthly cap, pool remaining)' },
          { label: 'Indemnity mode', expression: 'min(monthly cap × 80%, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity pays regardless of actual cost (capped at 80% of the monthly maximum) while the Face pool has balance."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost × <GrowthFactor rate="care-cost inflation" exponent="years from today" />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this care environment and policy year, with the daily equivalent beneath. MoneyGuard's daily benefit is set per care environment and inflates on the Face Amount rate."
        formulas={[
          {
            label: 'Monthly cap',
            expression: (
              <>
                at-issue daily for environment × days in month × <GrowthFactor rate="Face inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
        ]}
        notes="Face inflation capped at the configured cap year if specified."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Inflated Face + COB pool sizes at this policy year if no benefits were used. Pools keep growing on each future anniversary until any inflation cap is reached. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          {
            label: 'Face Amount',
            expression: (
              <>
                Face at issue × <GrowthFactor rate="Face inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
          {
            label: 'COB',
            expression: (
              <>
                COB at issue × <GrowthFactor rate="COB inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
          { label: 'Total', expression: 'Face value + COB value' },
        ]}
        notes="A 0% inflation rider on a pool leaves that pool flat. Once a pool's inflation cap is reached, its value is a true ceiling."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the Face + COB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (Face depletion + COB depletion)' },
          { label: 'Identity', expression: 'Pool limit − Drawn = Pool remaining (while pool is open)' },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. MoneyGuard depletes the death benefit dollar-for-dollar by Face Amount drawdown only; COB drawdown does not reduce the death benefit."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue Face Amount − Σ (Face drawdown)' },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound growth',
    benefitInflationCapNote: capYear => `Inflation capped at year ${capYear}`,
  },
};
